<template>
  <div>
    <div class="nav-bar-container">
      <div class="nav-bar">
        <div>
          <span
            v-if="isNewUser"
            class="nav-header"
          >
            {{ $i18n.t('settings.password.createPassword.welcomePrefix') }}
          </span>
          <span class="nav-name">{{ userName }}</span>
          <span
            v-if="isNewUser"
            class="nav-header"
          >{{ $i18n.t('settings.password.createPassword.comma') }}</span>
        </div>
        <span
          v-if="isNewUser"
          class="nav-header"
        >
          {{ $i18n.t('settings.password.createPassword.welcome') }}
        </span>
        <a
          v-if="showBackButton()"
          @click="navBack()"
        >
          &lt; {{ $i18n.t( 'back') }}</a>
      </div>
    </div>

    <div class="reset-container">
      <div class="header">
        <span
          v-if="isNewUser"
          class="notice-header"
        >
          {{ $i18n.t('settings.password.createPassword.proceedHead') }}
        </span>
        <span
          v-else
          class="notice-header"
        >
          {{ $i18n.t('settings.password.forgotPassword.proceedHead') }}
        </span>
        <hr style="max-width: 100%; width: 100%">
      </div>

      <form
        data-abide
        novalidate
        id="confirm-pwd-form"
        class="reset-pwd-form"
        @submit.prevent
      >
        <label class="required-flag">
          {{ $t('settings.password.newPassword') }}
        </label>
        <div class="password-input-container">
          <input
            v-model="newPassword"
            class="text-input password-input"
            :type="isNewPasswordVisible ? 'text' : 'password'"
            required
            @focus="showPasswordHint = true"
            @blur="showPasswordHint = false"
          >
          <button
            type="button"
            class="password-visibility-toggle"
            :aria-label="isNewPasswordVisible ? 'Hide new password' : 'Show new password'"
            :aria-pressed="isNewPasswordVisible.toString()"
            :title="isNewPasswordVisible ? 'Hide new password' : 'Show new password'"
            @click="toggleNewPasswordVisibility"
          >
            <svg
              v-if="isNewPasswordVisible"
              class="password-visibility-icon"
              aria-hidden="true"
              width="40"
              height="38"
              viewBox="0 0 40 38"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10 19.4179C10 19.4179 13.5496 13.4151 20 13.4151C26.4504 13.4151 30 19.4179 30 19.4179"
                stroke="#0B3A59"
                stroke-width="1.6"
                stroke-linejoin="round"
              />
              <path
                d="M23.5837 19.4179C23.5837 21.3899 21.9793 22.9884 20 22.9884"
                stroke="#0B3A59"
                stroke-width="1.6"
                stroke-linejoin="round"
              />
              <path
                d="M17.368 21.8435C16.776 21.2066 16.4141 20.3543 16.4141 19.4179C16.4141 17.4459 18.0185 15.8473 19.9977 15.8473C20.87 15.8473 21.6692 16.1578 22.2907 16.6739"
                stroke="#0B3A59"
                stroke-width="1.6"
                stroke-linejoin="round"
              />
              <path
                d="M27.5473 12.3351L13.2812 25.6649"
                stroke="#0B3A59"
                stroke-width="1.6"
                stroke-linejoin="round"
              />
            </svg>
            <svg
              v-else
              class="password-visibility-icon"
              aria-hidden="true"
              width="40"
              height="38"
              viewBox="0 0 40 38"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10 19.4768C10 19.4768 13.5496 13.4737 20 13.4737C26.4504 13.4737 30 19.4765 30 19.4765"
                stroke="#0B3A59"
                stroke-width="1.6"
                stroke-linejoin="round"
              />
              <path
                d="M22.5357 22.0015C23.9352 20.6071 23.9352 18.3464 22.5357 16.952C21.1362 15.5576 18.8672 15.5576 17.4676 16.952C16.0681 18.3464 16.0681 20.6071 17.4676 22.0015C18.8672 23.3959 21.1362 23.3959 22.5357 22.0015Z"
                stroke="#0B3A59"
                stroke-width="1.6"
                stroke-linejoin="round"
              />
            </svg>
          </button>
        </div>
        <p
          class="pwd-hint-span"
          v-show="showPasswordHint"
        >{{ $i18n.t('settings.password.rule') }}</p>
        <label class="required-flag">
          {{ $t('settings.password.confirmPassword') }}
        </label>
        <div class="password-input-container">
          <input
            v-model="confirmPassword"
            class="text-input password-input"
            :type="isConfirmPasswordVisible ? 'text' : 'password'"
            required
            @focus="finishInput = false"
            @blur="finishInput = true"
          >
          <button
            type="button"
            class="password-visibility-toggle"
            :aria-label="isConfirmPasswordVisible ? 'Hide confirm password' : 'Show confirm password'"
            :aria-pressed="isConfirmPasswordVisible.toString()"
            :title="isConfirmPasswordVisible ? 'Hide confirm password' : 'Show confirm password'"
            @click="toggleConfirmPasswordVisibility"
          >
            <svg
              v-if="isConfirmPasswordVisible"
              class="password-visibility-icon"
              aria-hidden="true"
              width="40"
              height="38"
              viewBox="0 0 40 38"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10 19.4179C10 19.4179 13.5496 13.4151 20 13.4151C26.4504 13.4151 30 19.4179 30 19.4179"
                stroke="#0B3A59"
                stroke-width="1.6"
                stroke-linejoin="round"
              />
              <path
                d="M23.5837 19.4179C23.5837 21.3899 21.9793 22.9884 20 22.9884"
                stroke="#0B3A59"
                stroke-width="1.6"
                stroke-linejoin="round"
              />
              <path
                d="M17.368 21.8435C16.776 21.2066 16.4141 20.3543 16.4141 19.4179C16.4141 17.4459 18.0185 15.8473 19.9977 15.8473C20.87 15.8473 21.6692 16.1578 22.2907 16.6739"
                stroke="#0B3A59"
                stroke-width="1.6"
                stroke-linejoin="round"
              />
              <path
                d="M27.5473 12.3351L13.2812 25.6649"
                stroke="#0B3A59"
                stroke-width="1.6"
                stroke-linejoin="round"
              />
            </svg>
            <svg
              v-else
              class="password-visibility-icon"
              aria-hidden="true"
              width="40"
              height="38"
              viewBox="0 0 40 38"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10 19.4768C10 19.4768 13.5496 13.4737 20 13.4737C26.4504 13.4737 30 19.4765 30 19.4765"
                stroke="#0B3A59"
                stroke-width="1.6"
                stroke-linejoin="round"
              />
              <path
                d="M22.5357 22.0015C23.9352 20.6071 23.9352 18.3464 22.5357 16.952C21.1362 15.5576 18.8672 15.5576 17.4676 16.952C16.0681 18.3464 16.0681 20.6071 17.4676 22.0015C18.8672 23.3959 21.1362 23.3959 22.5357 22.0015Z"
                stroke="#0B3A59"
                stroke-width="1.6"
                stroke-linejoin="round"
              />
            </svg>
          </button>
        </div>
        <p
          class="pwd-error-span"
          v-show="!validForm&&confirmPassword&&finishInput"
        >{{ $i18n.t('settings.password.errorHint') }}</p>
        <div class="btns-set">
          <!-- <button class="btn btn--darkblue  radius round" type="button" id="forgotpwd-form-cancel" @click="navBack()">Cancel</button> -->
          <button
            class="btn btn--orange  radius round"
            type="submit"
            id="forgotpwd-form-send"
            @click="send()"
            :disabled="!validForm"
          >{{ $i18n.t('action.confirm') }}</button>
        </div>
      </form>
    </div>

    <modal
      modalId="reset-exception-modal"
      ref="resetExceptionModal"
    >
      <div slot="content">
        <pre class="modal-pure-text">{{ errMsg || $t('settings.password.cannotReset') }}</pre>
      </div>
      <div slot="btns">
        <button
          class="btn btn--orange"
          @click="resetExceptionModal.close()"
        >
          {{ $t('action.ok') }}
        </button>
      </div>
    </modal>
    <modal
      modalId="confirm-reset-pwd-modal"
      ref="confirmResetPwdModal"
    >
      <div slot="content">
        <pre class="modal-pure-text">{{ $t('settings.password.passwordChangeSuccess') }}</pre>
      </div>
      <div slot="btns">
        <button
          class="btn btn--orange"
          @click="navToLogin()"
        >
          {{ $t('action.ok') }}
        </button>
      </div>
    </modal>
  </div>
</template>

<script>
import HTTP from '@/utils/http'
import Status from 'http-status-codes'
import notification from '@/utils/notification'
import keyMirror from 'keymirror'
import Modal from '@/controls/Modal'
import { getRegionsAndSetCurrent, switchLangByRegion } from '../utils'

const resetUserType = keyMirror({
  new: null,
  update: null,
})

export default {
  components: {
    Modal,
  },
  data () {
    return {
      newPassword: null,
      confirmPassword: null,
      isNewPasswordVisible: false,
      isConfirmPasswordVisible: false,
      showPasswordHint: false,
      finishInput: false,
      errMsg: '',
    }
  },
  computed: {
    validForm () {
      const password = /(?=.{8,})((?=.*\d)(?=.*[a-z])(?=.*[A-Z])|(?=.*\d)(?=.*[a-zA-Z])(?=.*[\W_])|(?=.*[a-z])(?=.*[A-Z])(?=.*[\W_])).*/
      return this.newPassword &&
        password.test(this.newPassword) &&
        this.confirmPassword &&
        (this.newPassword === this.confirmPassword)
    },
    isNewUser () {
      return this.$router.currentRoute.query.type === resetUserType.new
    },
    userName () {
      return this.$router.currentRoute.query.username
    },
    token () {
      return this.$router.currentRoute.query.token
    },
  },
  async mounted () {
    this.initAbide()
    await this.initModals()
    const regionName = this.$router.currentRoute.query.region
    if (regionName) {
      await getRegionsAndSetCurrent(regionName)
      switchLangByRegion(regionName)
    }
    await this.checkTokenExpired()
  },
  methods: {
    showBackButton () {
      return this.$router.meta && Object.values(this.$router.meta).length > 0
    },
    navBack () {
      this.$router.goBack()
    },
    validate () {
      this.abide.validateForm()
      this.disabled = $('input.is-invalid-input').length !== 0
    },
    toggleNewPasswordVisibility () {
      this.isNewPasswordVisible = !this.isNewPasswordVisible
    },
    toggleConfirmPasswordVisibility () {
      this.isConfirmPasswordVisible = !this.isConfirmPasswordVisible
    },
    async initModals () {
      this.resetExceptionModal = this.$refs.resetExceptionModal
      this.confirmResetPwdModal = this.$refs.confirmResetPwdModal
    },
    initAbide () {
      this.form = $('#confirm-pwd-form')
      if (!this.abide) {
        this.abide = new Foundation.Abide(this.form, {
          liveValidate: true,
        })
      }
    },
    navToLogin () {
      this.confirmResetPwdModal.close()
      this.$router.push({ name: 'publicLogin' })
    },
    navToLoginPage () {
      this.resetExceptionModal.close()
      this.$router.push({ name: 'publicLogin' })
    },
    async send () {
      try {
        this.$root.$children[0].$refs.loadingLayer.$emit('startLoading')
        if (this.$router.currentRoute.query.token) {
          await HTTP.put(`/reset/${this.$router.currentRoute.query.token}`, { password: this.newPassword })
        } else {
          await HTTP.put(`/resetcontractorpwd`, { password: this.newPassword })
        }
        this.confirmResetPwdModal.open()
      } catch (err) {
        this.showError(err)
      } finally {
        this.$root.$children[0].$refs.loadingLayer.$emit('stopLoading')
      }
    },
    showError (err) {
      const status = deep(err, 'response.status')
      if ([Status.BAD_REQUEST, Status.UNPROCESSABLE_ENTITY, Status.FORBIDDEN,
        Status.CONFLICT].includes(status)) {
        const msg = this.$t(`settings.password.${status}`)
        this.errMsg = msg
        this.resetExceptionModal.open()
      } else {
        notification.handleResponseError(err)
      }
    },
    async checkTokenExpired () {
      let token = this.$router.currentRoute.query.token
      if (token) {
        this.$root.$children[0].$refs.loadingLayer.$emit('startLoading')
        try {
          await HTTP.get(`/reset/${token}`)
        } catch (err) {
          this.showError(err)
        } finally {
          this.$root.$children[0].$refs.loadingLayer.$emit('stopLoading')
        }
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~styles/popup';

.nav-bar {
  padding: 15px 24px;
  width: 100%;
  background: #EBEBEB;
}

.reset-container {
  max-width: 1440px;
  margin: 0 auto;
}

.header {
  margin-top: 20px;
}

.nav-name {
  color: #6CA6CD;
}

.nav-header, .notice-header, .nav-name {
  font-family: Georgia;
  font-weight: bold;
  font-size: 24px;
}

.notice-header {
  padding-left: 20px;
}

.notice-note {
  font-family: Verdana;
  font-size: 16px;
  padding-left: 20px;
}

.reset-pwd-form {
  width: 330px;
  margin: 100px auto;
}

label {
  font-family: Verdana;
  font-size: 16px;
  color: #0C3A58;
}

.text-input {
  color: #0C3A58;
}

.password-input-container {
  position: relative;
  width: 100%;
}

.password-input-container .password-input {
  width: 100%;
  padding-right: 48px;
}

.password-input::-ms-reveal {
  display: none;
}

.password-visibility-toggle {
  position: absolute;
  top: 1px;
  right: 1px;
  bottom: 1px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 46px;
  margin: 0;
  padding: 0;
  color: #0C3A58;
  background: transparent;
  border: 0;
  cursor: pointer;
}

.password-visibility-toggle:focus,
.password-visibility-toggle:hover {
  color: #0c2b46;
  background: transparent;
}

.btns-set {
  text-align: center;
  padding: 20px;
  .btn {
    margin-left: 10px;
    margin-right: 10px;
  }
}

.pwd-hint-span {
  padding-bottom: 10px;
}
.pwd-error-span {
  color: red;
}

.login-link {
  margin: 22px 0;
  a {
    color: #0c2b46;
    text-decoration: underline;
  }
}
</style>
<style scoped src="styles/dashboard/style.scss"></style>
